import type { ClientConfigResponse } from '../../types/client-config'

export interface LoadClientConfigOptions {
  /**
   * Client key identifying the remote config document. When absent, the loader
   * short-circuits and returns `null` (the SDK proceeds with inline/defaults).
   */
  clientKey?: string
  /** Environment stamped onto the request as `&env=`. */
  env: string
  /**
   * Fully-resolved base URL for the client-config endpoint (path included).
   * The loader appends `?key=<clientKey>&env=<env>&host=<hostname>`. Typically
   * produced by `resolveClientConfigUrl(env, override)`.
   */
  url: string
  /**
   * Hostname do documento pedido. No browser é omitido e sai de
   * `location.hostname`; num render de SERVIDOR não existe `location`, e sem
   * este campo a requisição iria sem `host`.
   *
   * Não é detalhe: o rum-api gateia os destinations POR HOST, e o host também
   * entra na chave de cache da edge (ver {@link currentHost}). Sem ele o SSR
   * receberia um documento diferente do que o mesmo visitante receberia no
   * cliente — e as duas metades da mesma página discordariam.
   */
  host?: string
  /** Fetch timeout in ms. Default: 5000. */
  timeoutMs?: number
  /** Optional caller-supplied abort signal. */
  signal?: AbortSignal
  /**
   * Optional pre-fetched response — typically a `Promise<unknown>` started by
   * the CDN snippet **in parallel** with the SDK script download and stored on
   * `window.__MONTILLA_CONFIG_PROMISE__`. When it resolves to a valid document
   * the loader skips its own fetch. On rejection or invalid shape it falls
   * through to the normal fetch path.
   */
  prefetched?: Promise<unknown>
}

/**
 * Load the remote per-client config document.
 *
 * Behavior:
 * - No `clientKey` → returns `null` immediately (no network). This is what
 *   makes the whole feature opt-in and fully backward compatible.
 * - Tries the pre-fetched promise first (reuses the snippet's parallel fetch),
 *   then falls back to its own `GET <url>?key=&env=` request.
 * - On ANY failure (network, non-2xx, parse, unknown shape) it returns `null`
 *   and never throws — the SDK degrades to inline config + defaults.
 */
export async function loadClientConfig(
  opts: LoadClientConfigOptions,
): Promise<ClientConfigResponse | null> {
  const { clientKey, env, url, host, timeoutMs = 5000, signal, prefetched } = opts

  if (!clientKey) return null

  // Try the pre-fetched promise first — set by the CDN snippet in parallel
  // with the SDK script download. Saves one round-trip when honored.
  if (prefetched) {
    try {
      const payload = await prefetched
      const parsed = parse(payload)
      if (parsed) return parsed
    } catch (err) {
      console.warn('[montilla] Prefetched client config rejected — falling back to network fetch.', err)
    }
  }

  try {
    return await fetchRemote(url, clientKey, env, host ?? currentHost(), timeoutMs, signal)
  } catch (err) {
    console.warn('[montilla] Failed to fetch client config — using inline config + defaults.', err)
    return null
  }
}

/**
 * Hostname da página, para o rum-api gatear os destinations E para a resposta
 * poder ser cacheada na edge.
 *
 * Por que na URL e não só no header: o documento varia por host, e o Cloudflare
 * não indexa `Vary: Origin` — enquanto o host viesse apenas do `Origin`, o
 * rum-api tinha de responder `private` e NENHUM boot de SDK aproveitava cache
 * compartilhado (observado em produção como `cf-cache-status: DYNAMIC`). Com o
 * host na URL a chave de cache passa a carregá-lo.
 *
 * `location.hostname` já vem sem porta e em minúsculas. O filtro segue o mesmo
 * formato que o servidor aceita: assim SDK e rum-api concordam sobre o que é um
 * host válido, e não mandamos param que seria descartado (`localhost`, IP).
 *
 * Fora do browser devolve `null`, e quem renderiza no servidor passa o host por
 * `options.host` — mesmo filtro, aplicado em {@link normalizeHost}.
 */
function currentHost(): string | null {
  if (typeof window === 'undefined') return null
  return normalizeHost(window.location?.hostname)
}

/** Minúsculas e sem porta, ou `null`. Um filtro só para browser e servidor. */
function normalizeHost(raw?: string | null): string | null {
  const host = raw?.toLowerCase().replace(/:\d+$/, '')
  return host && /^[a-z0-9.-]+\.[a-z]{2,}$/.test(host) ? host : null
}

async function fetchRemote(
  url: string,
  clientKey: string,
  env: string,
  rawHost: string | null,
  timeoutMs: number,
  externalSignal?: AbortSignal,
): Promise<ClientConfigResponse | null> {
  const sep = url.includes('?') ? '&' : '?'
  // ORDEM FIXA de params (key, env, host): o Cloudflare indexa a query string
  // como escrita, e o purge do admin-api reconstrói esta mesma URL. Trocar a
  // ordem aqui purgaria uma URL que ninguém pediu.
  const host = normalizeHost(rawHost)
  const fullUrl =
    `${url}${sep}key=${encodeURIComponent(clientKey)}&env=${encodeURIComponent(env)}` +
    (host ? `&host=${encodeURIComponent(host)}` : '')

  const controller = new AbortController()
  const timer = setTimeout(() => controller.abort(), timeoutMs)
  externalSignal?.addEventListener('abort', () => controller.abort(), { once: true })

  try {
    const response = await fetch(fullUrl, { signal: controller.signal })
    if (!response.ok) {
      throw new Error(`HTTP ${response.status}`)
    }
    const json: unknown = await response.json()
    return parse(json)
  } finally {
    clearTimeout(timer)
  }
}

/**
 * Structurally validate an unknown payload into a `ClientConfigResponse`.
 * Lenient by design: accepts any object carrying at least one of the known
 * top-level blocks (`client` / `sdk` / `destinations`). Returns `null` for
 * anything else so the caller degrades to inline/defaults.
 */
function parse(payload: unknown): ClientConfigResponse | null {
  if (typeof payload !== 'object' || payload === null) return null
  const obj = payload as Record<string, unknown>
  const hasClient = typeof obj.client === 'object' && obj.client !== null
  const hasSdk = typeof obj.sdk === 'object' && obj.sdk !== null
  const hasDestinations = Array.isArray(obj.destinations)
  if (!hasClient && !hasSdk && !hasDestinations) return null
  return payload as ClientConfigResponse
}
